import { Select, SelectProps } from 'antd';
import { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import { ReactElement } from 'react';
import useReadonly from 'src/hook/useReadonly';
import { DomUtil } from 'src/util/DomUtil';
import "./select.less";

const DEFAULT_SHOW_SEARCH: SelectProps['showSearch'] = { optionFilterProp: 'label' } as const;

function getTableEl(triggerNode: HTMLElement) {
  const tableElement = triggerNode.closest<HTMLElement>('.ant-table');

  if (tableElement) {
    const isInExpandedRow =
      triggerNode.closest('.ant-table-row-expand-icon-cell') ||
      triggerNode.closest('.ant-table-expanded-row');

    if (!isInExpandedRow) {
      return;
    }

    let container: HTMLElement | null =
      tableElement.closest<HTMLElement>('.ant-table-container') ||
      tableElement.closest<HTMLElement>('.ant-table-wrapper') ||
      tableElement;

    while (container) {
      const position = window.getComputedStyle(container).position;
      if (
        position === 'relative' ||
        position === 'absolute' ||
        position === 'fixed' ||
        container === document.body
      ) {
        break;
      }
      container = container.parentElement;
    }

    if (container && container !== document.body) {
      container.style.position = 'relative';
      container.style.overflow = 'visible';
      return container;
    }
  }
}

const getPopupContainer: SelectProps['getPopupContainer'] = (triggerNode: HTMLElement): HTMLElement => {

  const tbl = getTableEl(triggerNode);
  if (tbl) return tbl;

  const modalContent = triggerNode.closest<HTMLElement>('.ant-modal-container');
  if (modalContent && triggerNode.parentElement) {
    return triggerNode.parentElement;
  }

  const scrollableContainer = DomUtil.closestScrollable(triggerNode);
  if (scrollableContainer && scrollableContainer instanceof HTMLElement) {
    return scrollableContainer;
  }

  const drawerContent = triggerNode.closest<HTMLElement>('.ant-drawer-content');
  if (drawerContent) {
    return drawerContent;
  }

  const card = triggerNode.closest<HTMLElement>('.ant-card');
  if (card) {
    return card;
  }

  return triggerNode.parentElement || document.body;
};

/**
 * AntD Select with some default values
 * @param props AntD Select properties
 * @param ignoreReadonly optional disable of readonly behavior
 * @returns
 */
export const XmSelect: <ValueType = unknown, OptionType extends DefaultOptionType | BaseOptionType = DefaultOptionType>(
  props: SelectProps<ValueType, OptionType> & { ignoreReadonly?: boolean; }
) => ReactElement = ({ ignoreReadonly, disabled, showSearch, ...props }) => {
  const { isDisabled } = useReadonly(ignoreReadonly, disabled);

  return <Select
    classNames={{ root: 'xm-select', popup: { root: 'xm-select-dropdown' } }}
    styles={{ content: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' } }}
    getPopupContainer={getPopupContainer}
    popupMatchSelectWidth={false}
    showSearch={showSearch == null ? DEFAULT_SHOW_SEARCH
      : typeof showSearch === 'object' ? { ...DEFAULT_SHOW_SEARCH, ...showSearch }
        : showSearch}
    maxTagPlaceholder={omitted => `+${omitted.length}`}
    {...props}
    disabled={isDisabled}
  />;
};
